import * as React from 'react';
import FlexboxContainerComponent from "components/UI/FlexboxContainer";
import { Color } from "lib/styles";
import BrandFileUpload from "./BrandFileUpload";
import EditorSection from "view/EditorSection";
import { validateBrandName, validateBrandTitle } from "utils/brand";
import RichTextEditor from "view/RichTextEditor";
import { isVendor } from "data/User";
import Checkbox from "components/UI/Checkbox";
import styled from "styled-components";
import Brand from "data/Brand";
import Vendor from "data/Vendor";
import InputComponent from "components/UI/Input";
import { useState } from "react";
import { Main } from "styles/themeColors";
import { useAuth } from "lib/Auth";
import FeatureFlag from 'data/FeatureFlag';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';

const ErrorMessage = styled.p`
    color: ${Color.error};
    font-size: 12px;
    margin-top: 2px;
`

interface Props {
    brand: Brand;
    selectedVendor: Vendor;
    vendorsList?: Vendor[];
    updateVendor: (name: string) => void;
    setBrand: (brand: Brand) => void;
    brandsList?: Brand[];
    errors: any;
    setErrors: any;
    hasEditRights?: boolean;
    onBrandTitleBlur: (value: string) => void;
    onBrandDescBlur: (value: string) => void;
    featureFlags: FeatureFlag;
};

const BrandDetailsConfig = (props: Props) => {
    const { brand, selectedVendor, setBrand, errors, setErrors, hasEditRights, onBrandDescBlur, featureFlags } = props;
    const [isTrusted, setIsTrusted] = useState<boolean>(!!brand.trustedSince);
    const { user } = useAuth();

    const { readOnly: isAddDetailsReadOnly } = useReadPermission(PermissionCode.BRANDS_ADD_DETAILS);

    React.useEffect(() => {
        setIsTrusted(!!brand.trustedSince)
    }, [brand]);

    const handleTrustedSince = (val: any) => {
        console.log(val);
        if (!val) {
            setBrand({ ...brand, trustedSince: '' });
        }
        setIsTrusted(val);
    }

    return (
        <FlexboxContainerComponent padding='10px 24px 10px 24px' width='100%'>
            <BrandFileUpload brand={brand} setBrand={setBrand} selectedVendor={selectedVendor} hasEditRights={hasEditRights} featureFlags={featureFlags} error={errors.media} />

            <FlexboxContainerComponent flexDirection='column' width='70%' >
                <EditorSection
                    shouldShowValidationStatus={false}
                    noBorder={true}
                    label="Brand name"
                    disablePaddingBottom={true}
                    isInvalid={!!errors.name}
                >

                    <InputComponent
                        id='brand-name-id'
                        placeholder='Enter your brand name (50 characters)'
                        value={brand.name}
                        width="100%"
                        name="brand-name"
                        onInputChange={(name) => {
                            const _errors = validateBrandName(name);
                            if (_errors.name) {
                                setErrors({ ...errors, ..._errors });
                            } else {
                                setErrors({ ...errors, name: '' });
                            }
                            setBrand({ ...brand, name })
                        }
                        }
                        labelText={!!errors.name ? '' : 'Brand name you would like to display on the storefront'}
                        productRedesignFlag
                        isInvalid={!!errors.name}
                        errorMessage={errors.name}
                        disabled={!hasEditRights}
                        readOnly={isAddDetailsReadOnly}
                    />

                </EditorSection>

                <EditorSection
                    shouldShowValidationStatus={false}
                    noBorder={true}
                    label="Brand title"
                    isOptional={true}
                    disablePaddingBottom={true}
                    productRedesignFlag
                    isInvalid={!!errors.title}
                >
                    <InputComponent
                        id='brand-name-title'
                        placeholder='Enter your brand title'
                        value={brand.title || ''}
                        width="100%"
                        name="brand-title"
                        onInputChange={(title) => {
                            const _errors = validateBrandTitle(title);
                            if (_errors.title) {
                                setErrors({ ...errors, ..._errors });
                            } else {
                                setErrors({ ...errors, title: '' });
                            }
                            setBrand({ ...brand, title })
                        }
                        }
                        productRedesignFlag
                        isInvalid={!!errors.title}
                        errorMessage={errors.title}
                        disabled={!hasEditRights}
                        readOnly={isAddDetailsReadOnly}
                    />

                </EditorSection>

                <EditorSection
                    shouldShowValidationStatus={false}
                    noBorder={true}
                    label="Brand description"
                    isOptional={true}
                    disablePaddingBottom={true}
                    productRedesignFlag
                    isInvalid={!!errors.description}
                >
                    <RichTextEditor
                        toolbarId='brand-description-toolbar'
                        onBlur={onBrandDescBlur}
                        placeholder="Enter your brand description"
                        value={brand.description || ''}
                        maxLength={5000}
                        showCharactersCount={!errors.description}
                        isInvalid={!!errors.description}
                        disabled={!hasEditRights}
                        productRedesignFlag
                        permissionCode={PermissionCode.BRANDS_ADD_DETAILS}
                    />
                    {errors.description && <ErrorMessage>{errors.description}</ErrorMessage>}

                </EditorSection>


                {!isVendor(user!) && <EditorSection
                    noBorder={true}
                    disablePaddingBottom={true}
                >
                    <FlexboxContainerComponent width='100%' margin='0'>
                        <Checkbox
                            id='brand-trusted-since'
                            appearance="none"
                            className='align-checkbox'
                            checkboxSize="s"
                            checked={isTrusted}
                            label="Trusted brand on OFMP since"
                            onChange={handleTrustedSince}
                            theme={isTrusted ? 'brandLight' : 'lightGray'}
                            bgColor={isTrusted ? Main.brandLight : Main.accent}
                            permissionCode={PermissionCode.BRANDS_ADD_DETAILS}
                        />

                        {/** Input field on enter year of trusted since */}
                        <ReadOnlyWrapper readOnly={isAddDetailsReadOnly}>
                        <input
                            type="number"
                            placeholder="Enter year"
                            value={brand.trustedSince}
                            disabled={isAddDetailsReadOnly}
                            onChange={isAddDetailsReadOnly ? undefined : e => setBrand({ ...brand, trustedSince: e.target.value })}
                            className="year-input"
                            maxLength={4}
                            style={{ width: '100px', border: '1px solid #ccc', borderRadius: '5px', padding: '5px' }}
                        />
                        </ReadOnlyWrapper>
                    </FlexboxContainerComponent>
                </EditorSection>
                }
            </FlexboxContainerComponent>
        </FlexboxContainerComponent>
    );
}

export default BrandDetailsConfig;